<script setup>
import { computed } from 'vue';
import { useApiData } from '../../composables/useApiData';
import { usePortalAccount } from '../../composables/usePortalAccount';

const props = defineProps({
  widget: { type: Object, required: true },
  widgetStyle: { type: Object, default: () => ({}) },
});

const { accountId } = usePortalAccount();
const { data, loading, error } = useApiData(
  computed(() => props.widget),
  computed(() => accountId.value)
);

const title = computed(() => props.widget.props?.title || 'Metric');
const icon = computed(() => props.widget.props?.icon || 'i-lucide-activity');
const trend = computed(
  () => data.value?.mapped?.trend || props.widget.props?.trend || 'neutral'
);

const formattedValue = computed(() => {
  const val = data.value?.mapped?.value ?? props.widget.props?.value ?? '0';
  const format = props.widget.props?.format;
  const num =
    typeof val === 'string'
      ? parseFloat(val.replace(/\s/g, '').replace(/,/g, ''))
      : val;
  if (isNaN(num)) return val;
  if (format === 'currency') return `$${num.toLocaleString('en-US')}`;
  if (format === 'percentage') return `${num.toLocaleString('en-US')}%`;
  return num.toLocaleString('en-US');
});

const trendConfig = computed(() => {
  if (trend.value === 'up')
    return {
      icon: 'i-lucide-arrow-up-right',
      color: 'text-emerald-600',
      bgColor: 'bg-emerald-50',
      iconColor: 'text-emerald-500',
    };
  if (trend.value === 'down')
    return {
      icon: 'i-lucide-arrow-down-right',
      color: 'text-rose-600',
      bgColor: 'bg-rose-50',
      iconColor: 'text-rose-500',
    };
  return {
    icon: 'i-lucide-minus',
    color: 'text-slate-600',
    bgColor: 'bg-slate-50',
    iconColor: 'text-slate-500',
  };
});
</script>

<template>
  <div class="relative h-full flex flex-col justify-between p-6 group/metric">
    <!-- Header avec icône et titre -->
    <div class="flex items-start justify-between mb-4">
      <div class="flex items-center gap-3">
        <div
          class="w-10 h-10 rounded-xl flex items-center justify-center transition-all duration-300 group-hover/metric:scale-110 shadow-sm"
          :class="trendConfig.bgColor"
        >
          <span class="text-base" :class="[icon, trendConfig.iconColor]" />
        </div>
        <div class="flex flex-col gap-0.5">
          <span
            class="text-[10px] font-black text-slate-400 uppercase tracking-[0.15em] leading-none"
          >
            {{ title }}
          </span>
          <div class="h-px w-8 bg-slate-200" />
        </div>
      </div>

      <!-- Loading Indicator -->
      <div v-if="loading" class="w-5 h-5">
        <div
          class="w-full h-full border-2 border-slate-100 border-t-indigo-500 rounded-full animate-spin"
        />
      </div>
    </div>

    <!-- Valeur principale -->
    <div class="mb-4">
      <h2
        class="text-3xl font-black text-slate-900 leading-none tracking-tighter tabular-nums"
      >
        {{ formattedValue }}
      </h2>
    </div>

    <!-- Footer avec trend -->
    <div
      class="flex items-center justify-between pt-4 border-t border-slate-100"
    >
      <div
        class="flex items-center gap-1.5 font-black text-[11px] px-2.5 py-1 rounded-lg transition-all"
        :class="[trendConfig.color, trendConfig.bgColor]"
      >
        <span :class="trendConfig.icon" class="text-xs" />
        <span>{{
          data?.mapped?.subvalue || props.widget.props?.subvalue || '+0%'
        }}</span>
      </div>
      <span class="text-[9px] font-bold text-slate-400 uppercase tracking-wider">vs last month</span>
    </div>

    <!-- Error State -->
    <div
      v-if="error"
      class="absolute inset-0 flex items-center justify-center bg-rose-50/50 rounded-2xl"
    >
      <div class="text-center px-4">
        <span class="i-lucide-alert-circle text-rose-500 text-lg mb-2 block" />
        <p class="text-xs font-bold text-rose-600">Error loading data</p>
      </div>
    </div>
  </div>
</template>
